import * as React from 'react'

import { useMutation } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  BulkAssignItAssetsDocument,
  type BulkAssignItAssetsMutation,
  type BulkAssignItAssetsMutationVariables,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { type Location, LocationSelector } from 'modules/it_management/components/LocationSelector'

interface Props {
  ids: string[]
  onClose: () => void
}

export const BulkAssignItAssetsModal = ({ ids, onClose }: Props) => {
  const [bulkAssignItAssets, { loading: bulkAssignLoading }] = useMutation<
    BulkAssignItAssetsMutation,
    BulkAssignItAssetsMutationVariables
  >(BulkAssignItAssetsDocument)

  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number>()
  const [selectedLocation, setSelectedLocation] = React.useState<Location | null>(null)

  const handleBulkAssignAssets = async () => {
    try {
      await bulkAssignItAssets({
        variables: {
          ids: ids,
          ownerId: selectedEmployeeId ? selectedEmployeeId.toString() : null,
          locationId: selectedLocation ? selectedLocation.id.toString() : null,
        },
        refetchQueries: ['ItAssetsTable', 'ItAssetDetails', 'ItAssetsStatusCounts'],
        awaitRefetchQueries: true,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('it_management.assets.bulk_assign.success_message'),
      })
      onClose()
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.assets.bulk_assign.error_message'),
      })
    }
  }

  const assetCount = ids.length

  return (
    <Modal
      title={
        assetCount > 1
          ? i18n.t('it_management.assets.bulk_assign.modal_title_plural', { count: assetCount })
          : i18n.t('it_management.assets.bulk_assign.modal_title_single', { count: assetCount })
      }
      onClose={onClose}
      illustration={'user'}
      description={
        assetCount > 1
          ? i18n.t('it_management.assets.bulk_assign.modal_description_plural', {
              count: assetCount,
            })
          : i18n.t('it_management.assets.bulk_assign.modal_description_single', {
              count: assetCount,
            })
      }
      onSubmit={handleBulkAssignAssets}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} disabled={bulkAssignLoading}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="primary" submit disabled={bulkAssignLoading}>
            {bulkAssignLoading
              ? i18n.t('it_management.assets.bulk_assign.assigning')
              : i18n.t('it_management.assets.bulk_assign.assign')}
          </Button>
        </Box>
      }
    >
      <Box gap="s32" maxWidth="s400">
        <Box gap="s16">
          <Box gap="s8">
            <Text.Normal size="200" weight="medium" color="grey800">
              {i18n.t('it_management.assignment_step.employee')}
            </Text.Normal>
            <EmployeeSelectorV2 value={selectedEmployeeId} onChange={setSelectedEmployeeId} />
          </Box>

          <LocationSelector
            value={selectedLocation}
            onSelect={setSelectedLocation}
            label={i18n.t('it_management.assignment_step.space')}
            emptyLabel={i18n.t('it_management.assignment_step.empty_label')}
          />
        </Box>
      </Box>
    </Modal>
  )
}
